import { useState, useEffect, useCallback, useRef } from 'react';
import { useEmbedAwareNavigate as useNavigate } from './useEmbedAwareNavigate';
import { useFeature } from './useFeature';
import useToast from './useToast';
import { toastMessageConstants } from '../constants/globalConstant';

/**
 * Manages builder data lifecycle: fetch by ID, auto-create, navigate, mutate.
 *
 * `config.apis.loadBuilderData(id)` must return the data directly (or null on failure).
 * It can be a single API call or orchestrate multiple calls — the hook doesn't care.
 *
 * @param {Object} [options]
 * @returns {{
 *   data: any,
 *   status: string,
 *   isSaving: boolean,
 *   configId: string|null,
 *   patchData: Function,
 *   updateItem: Function,
 *   updateName: Function,
 *   mutate: Function,
 *   refetch: Function,
 * }}
 */
export function useBuilderData({ skipCreate = false } = {}) {
  const feature = useFeature();
  const navigate = useNavigate();
  const toast = useToast();
  const { config, configId, createItem, setPublishStatus } = feature;

  // Latest-value refs so the once-only auto-create effect below doesn't need
  // createItem/navigate as deps. When they were deps, any context re-render
  // during the in-flight POST re-ran the effect cleanup, which cancelled the
  // continuation — the homepage got created but the builder never navigated
  // to it and the loader spun forever.
  const createItemRef = useRef(createItem);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    createItemRef.current = createItem;
    navigateRef.current = navigate;
  });


  const [data, setData] = useState(null);
  const [status, setStatus] = useState(configId ? 'loading' : 'idle');
  const [isSaving, setIsSaving] = useState(false);
  const savingCountRef = useRef(0);

  const beginSave = useCallback(() => {
    savingCountRef.current += 1;
    if (savingCountRef.current === 1) setIsSaving(true);
  }, []);

  const endSave = useCallback(() => {
    savingCountRef.current = Math.max(0, savingCountRef.current - 1);
    if (savingCountRef.current === 0) setIsSaving(false);
  }, []);

  // ---- Fetch path ----
  const fetchItem = useCallback(
    async (id, signal) => {
      if (!config.apis.loadBuilderData) return;
      setStatus('loading');
      try {
        const result = await config.apis.loadBuilderData(id || configId);
        if (signal?.aborted) return;
        if (result) {
          setData(result);
          setStatus('success');
          // Sync publish status from fetched data (boolean)
          if (result.status) {
            setPublishStatus(result.status === 'published');
          }
        } else {
          setStatus('notFound');
        }
      } catch {
        if (signal?.aborted) return;
        setStatus('notFound');
      }
    },
    [config, configId, setPublishStatus]
  );

  // After auto-create we already have the new doc from the POST response;
  // skip the immediate GET that would otherwise fire when configId flips
  // from null → newId. Consumed (cleared) on the very next fetch effect run.
  const skipNextFetchRef = useRef(false);

  // Fetch when we have a configId
  useEffect(() => {
    if (!configId) return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    const controller = new AbortController();
    fetchItem(configId, controller.signal);
    return () => controller.abort();
  }, [configId, fetchItem]);

  // ---- Auto-create path ----
  // Runs exactly once when no configId is present (new-builder route).
  const createAttempted = useRef(false);

  useEffect(() => {
    if (configId) return;
    if (skipCreate) return;
    if (config.skipAutoCreate) return;
    if (!config.apis.create) return;
    if (createAttempted.current) return;
    createAttempted.current = true;

    // Guard against the user leaving the page mid-create by comparing the
    // URL, NOT by effect-cleanup/unmount flags: React 18 runs effect
    // cleanups whenever a lazy chunk re-suspends the route's Suspense
    // boundary, so "cleanup ran" does not mean "user left" — relying on it
    // dropped the post-create navigation and left the loader spinning.
    const startPath = window.location.pathname;

    (async () => {
      setStatus('loading');
      const newItem = await createItemRef.current();
      if (window.location.pathname !== startPath) return; // user navigated away
      const newId = newItem?.[config.idKey] || newItem?.config?.[config.idKey] || newItem?.id;
      if (newId) {
        // Seed local state from the POST response so the post-navigate
        // fetch effect can skip the redundant GET. POST and GET return
        // the same doc shape per REST convention; if a backend ever
        // returns a partial doc here, the next user action's mutate()
        // response will fill it in.
        setData(newItem);
        setStatus('success');
        if (newItem.status) {
          setPublishStatus(newItem.status === 'published');
        }
        skipNextFetchRef.current = true;
        navigateRef.current(config.builderRoute(newId), { replace: true });
      } else {
        navigateRef.current(config.listingRoutes[0], { replace: true });
      }
    })();
  }, [
    configId,
    skipCreate,
    config.skipAutoCreate,
    config.apis.create,
    config.idKey,
    config.builderRoute,
    config.listingRoutes
  ]);

  // ---- Mutations ----

  /**
   * Merge a partial update into the current data (local only).
   * Internal — do not use for persisted updates. Prefer updateItem / mutate.
   */
  const patchData = useCallback((partial) => {
    setData((prev) => {
      const patch = typeof partial === 'function' ? partial(prev) : partial;
      return { ...(prev || {}), ...patch };
    });
  }, []);

  /**
   * Persisted update — calls config.apis.update(configId, body), replaces
   * local data with the server response, and returns the response.
   * Non-optimistic; caller awaits for the server before UI reflects change.
   *
   * On failure, returns null. The axios error interceptor has already
   * shown the toast — no local error handling needed here.
   */
  const updateItem = useCallback(
    async (body) => {
      if (!config.apis.update || !configId) return null;
      beginSave();
      try {
        const data = await config.apis.update(configId, body);
        if (data) setData(data);
        return data;
      } catch {
        // Interceptor already toasted.
        return null;
      } finally {
        endSave();
      }
    },
    [config, configId, beginSave, endSave]
  );

  // Some features (e.g. menu builder) have no update-name API — check config.apis.updateName
  const updateNameFn = useCallback(
    (name) => {
      const nameField = config.nameField || 'name';
      return updateItem({ [nameField]: name });
    },
    [updateItem, config.nameField]
  );
  const updateName = config.apis.updateName === null ? undefined : updateNameFn;

  /**
   * Centralised optimistic mutation with rollback.
   *
   *   await mutate({
   *     optimistic: (prev) => ({ ...prev, config: nextList }),
   *     request:    ()     => config.apis.update(configId, { config: nextList }),
   *     errorToast: "Couldn't save item",   // optional fallback message
   *   });
   *
   * On failure, previous data is restored. If the axios error interceptor
   * already surfaced the error (flagged via `_interceptorToasted`), we do
   * NOT toast again. Otherwise (pre-migration features whose API files
   * still swallow rejections) fall back to `errorToast` or the error message.
   */
  const mutate = useCallback(
    async ({ optimistic, request, errorToast, onSuccess }) => {
      let previous;
      setData((prev) => {
        previous = prev;
        return typeof optimistic === 'function' ? optimistic(prev) : optimistic;
      });
      beginSave();
      try {
        const result = await request();
        if (!result) throw new Error('Empty response');
        if (result && typeof result === 'object' && !Array.isArray(result)) {
          setData(result);
        }
        if (onSuccess) onSuccess(result);
        return result;
      } catch (err) {
        setData(previous);
        if (!err?._interceptorToasted) {
          toast.error(errorToast || err?.message || toastMessageConstants.SOMETHING_WENT_WRONG);
        }
        return null;
      } finally {
        endSave();
      }
    },
    [toast, beginSave, endSave]
  );

  /**
   * Shortcut for the common "persist a new `config` array with optimistic
   * update + rollback" mutation. Used by builders whose data payload has
   * a `config` field (launchbar, topnav, etc.).
   */
  const mutateConfig = useCallback(
    (nextConfig, { onSuccess, errorToast } = {}) =>
      mutate({
        optimistic: (prev) => ({ ...(prev || {}), config: nextConfig }),
        request: () => config.apis.update(configId, { config: nextConfig }),
        errorToast,
        onSuccess
      }),
    [mutate, config, configId]
  );

  return {
    data,
    status,
    isSaving,
    configId,
    patchData,
    updateItem,
    updateName,
    mutate,
    mutateConfig,
    refetch: fetchItem
  };
}
